import React from "react";
import { formatCurrency, calculateEffortDays, calculateScheduleWeeks } from "../view-util.js";

// Phase summary table component that shows totals by tag/phase
const PhaseTable = ({ flows, settings, className }) => {
  // Collect all tags and calculate totals for each
  const phaseData = {};
  
  flows.forEach(flow => {
    // Process flow-level tags
    if (flow.tags && flow.tags.length > 0) {
      flow.tags.forEach(tag => {
        if (!phaseData[tag]) {
          phaseData[tag] = { points: 0, cost: 0 };
        }
        // Add all stories in this flow to this tag
        flow.stories.forEach(story => {
          phaseData[tag].points += story.size || 0;
        });
      });
    }
    
    // Process story-level tags
    flow.stories.forEach(story => {
      if (story.tags && story.tags.length > 0) {
        story.tags.forEach(tag => {
          if (!phaseData[tag]) {
            phaseData[tag] = { points: 0, cost: 0 };
          }
          phaseData[tag].points += story.size || 0;
        });
      }
    });
  });
  
  // Calculate costs
  Object.keys(phaseData).forEach(tag => {
    phaseData[tag].cost = phaseData[tag].points * settings.pointPrice;
  });
  
  // Sort phases by points (descending)
  const sortedPhases = Object.entries(phaseData).sort((a, b) => b[1].points - a[1].points);
  
  // Don't show the table if there are no phases
  if (sortedPhases.length === 0) return null;
  
  return (
    <table className={`${className} mb-4 mt-5`}>
      <thead>
        <tr className="has-background-light">
          <th>Phase</th>
          {settings.showPoints && <th>Points</th>}
          {settings.showPrice && <th>Cost</th>}
          {settings.showPrice && settings.dayRate && settings.teamSize && <th>Timescale</th>}
          <th>Relative Size</th>
        </tr>
      </thead>
      <tbody>
        {sortedPhases.map(([tag, data]) => {
          const effortDays = calculateEffortDays(data.cost, settings.dayRate);
          const scheduleWeeks = calculateScheduleWeeks(effortDays, settings.teamSize);
          
          return (
            <tr key={tag}>
              <td className="has-text-weight-bold">{tag}</td>
              {settings.showPoints && <td>{data.points}</td>}
              {settings.showPrice && <td>{formatCurrency(data.cost, settings.currency)}</td>}
              {settings.showPrice && settings.dayRate && settings.teamSize && (
                <td>
                  {scheduleWeeks && effortDays 
                    ? `Approx ${scheduleWeeks} weeks (${effortDays} days effort)`
                    : '-'}
                </td>
              )}
              <td>
                <div
                  className="sparkline"
                  style={{
                    background: "hsl(171, 100%, 41%)",
                    marginTop: "0.2em",
                    height: "1.2em",
                    width: Math.max(data.points * 5, 5) + "px",
                  }}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

// Summary table component that shows section totals
const SummaryTable = ({ flows, settings, className, selectedTags = [] }) => {
  // Calculate section totals for each flow
  const calculateSectionTotal = (flow) => {
    // If flow has filtered stories, use those for calculations
    const storiesToCount = flow.stories.some(s => s.hasOwnProperty('visible')) 
      ? flow.stories.filter(story => story.visible !== false)
      : flow.stories;
      
    return storiesToCount.reduce((a, b) => a + (b.size || 0), 0);
  };
  
  // Calculate the total points across all flows
  const totalPoints = flows.reduce((total, flow) => {
    return total + calculateSectionTotal(flow);
  }, 0);
  
  return (
    <table className={`${className} mb-4 mt-5`}>
      <thead>
        <tr className="has-background-light">
          <th>Section</th>
          {settings.showPoints && <th>Points</th>}
          {settings.showPrice && <th>Cost</th>}
          <th>% of Total</th>
          <th>Relative Size</th>
        </tr>
      </thead>
      <tbody>
        {flows.map((flow) => {
          const sectionTotal = calculateSectionTotal(flow);
          const percentOfTotal = totalPoints > 0 ? ((sectionTotal / totalPoints) * 100).toFixed(1) : '0';
          const fadedStyle = flow.visible === false ? { opacity: 0.4 } : {};
          
          return (
            <tr key={flow.lineNo} style={fadedStyle}>
              <td className="has-text-weight-bold">{flow.name}</td>
              {settings.showPoints && <td>{sectionTotal}</td>}
              {settings.showPrice && <td>{formatCurrency(sectionTotal * settings.pointPrice, settings.currency)}</td>}
              <td>{percentOfTotal}%</td>
              <td>
                <div
                  className="sparkline"
                  style={{
                    background: "hsl(171, 100%, 41%)",
                    marginTop: "0.2em",
                    height: "1.2em",
                    width: Math.max(sectionTotal * 5, 5) + "px",
                  }}
                />
              </td>
            </tr>
          );
        })}
        <tr className="has-background-light">
          <td className="has-text-weight-bold">Total</td>
          {settings.showPoints && <td className="has-text-weight-bold">{totalPoints}</td>}
          {settings.showPrice && <td className="has-text-weight-bold">{formatCurrency(totalPoints * settings.pointPrice, settings.currency)}</td>}
          <td className="has-text-weight-bold">100%</td>
          <td></td>
        </tr>
      </tbody>
    </table>
  );
};

const EstimateTable = ({ flows, settings, className, selectedTags = [] }) => {
  // Calculate section totals for each flow
  const calculateSectionTotal = (flow) => {
    // If flow has filtered stories, use those for calculations
    const storiesToCount = flow.stories.some(s => s.hasOwnProperty('visible')) 
      ? flow.stories.filter(story => story.visible !== false)
      : flow.stories;
      
    return storiesToCount.reduce((a, b) => a + (b.size || 0), 0);
  };

  // Calculate totals for the summary
  const totalPoints = flows.reduce((total, flow) => {
    return total + calculateSectionTotal(flow);
  }, 0);
  const totalPrice = totalPoints * settings.pointPrice;
  const effortDays = calculateEffortDays(totalPrice, settings.dayRate);

  return (
    <div class="estimate-table">
    <h2>Summary</h2>      
      <SummaryTable 
        flows={flows} 
        settings={settings} 
        className={className} 
        selectedTags={selectedTags} 
      />
      <h2>Summary by Phase</h2>
      <PhaseTable 
        flows={flows} 
        settings={settings} 
        className={className} 
      />
      <h2>Details</h2>
      <table className={`${className} mt-2`}>
      <tbody>
        {flows.map((flow) => {
          // Always show all flows, just fade them if they don't have the selected tags
          
          const sectionTotal = calculateSectionTotal(flow);
          const fadedStyle = flow.visible === false ? { opacity: 0.4 } : {};
          
          return (
            <React.Fragment key={flow.lineNo}>
              <tr style={fadedStyle}>
                <td colSpan="5" className="has-background-light">
                  <strong style={{ color: '#000000' }}>{flow.name}</strong>
                </td>
              </tr>
              {flow.stories.map((story) => {
                // Determine if this story should be faded based on tag selection
                const shouldFade = selectedTags.length > 0 && 
                  !(story.tags && story.tags.some(tag => selectedTags.includes(tag)))
                   && !(flow.tags && flow.tags.some(tag => selectedTags.includes(tag)));
                //const shouldFade = selectedTags.length > 0;                
                const storyStyle = shouldFade ? { opacity: 0.25 } : {opacity: 1 };
                
                const tagHtml = story.tags ? story.tags.map(tag => (
                  <span key={tag} className="tag is-info is-light is-small">{tag}</span>
                )) : null;

                return (                  
                <tr key={story.text} style={storyStyle}>
                  <td className="has-text-weight-bold" dangerouslySetInnerHTML={{ __html: story.title }}></td>
                  <td>
                    <span dangerouslySetInnerHTML={{ __html: story.text }}></span>
                    {tagHtml && <span className="ml-2">{tagHtml}</span>}
                  </td>
                  {settings.showPoints && <td>{story.size}</td>}
                  {settings.showPoints && (
                    <td>
                      <div
                        className="sparkline"
                        style={{
                          background:
                            "hsl(0, 8.50%, " + 100 * (1 - story.size / 18) + "%)",
                          marginTop: "0.2em",
                          height: "1.2em",
                          width: story.size * 5 + "px",
                        }}
                      />
                    </td>
                  )}
                  {settings.showPrice && (
                    <td>{formatCurrency(story.size * settings.pointPrice, settings.currency)}</td>
                  )}
                  </tr>
                );
              })}
              <tr key={`${flow.name}-subtotal`} className="subtotal-row" style={{ fontWeight: 'bold', backgroundColor: '', borderBottom: '2px solid white' }}>
                <td colSpan={2} style={{ paddingBottom: '4em', textAlign: 'right' }}>Total</td>
                {settings.showPoints && <td>{sectionTotal}&nbsp;pts</td>}
                {settings.showPoints && (
                  <td>
                   
                  </td>
                )}
                {settings.showPrice && (
                  <td>{formatCurrency(sectionTotal * settings.pointPrice, settings.currency)}</td>
                )}
              </tr>
            </React.Fragment>
          );
        }).filter(Boolean)}
      </tbody>
    </table>
    </div>
  );
};

export default EstimateTable;
